/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment, useCallback, useState} from "react";
import {useSelector} from "react-redux";

import {service as PermitService} from "../../../Services/PermitService";
import {useSortedData} from "../../../Hooks/ListHooks";
import {MutableList} from "./MutableList";
import {useAutoCompleteOptionsFilterCallback} from "../../../Hooks/useAutoCompleteOptionsFilterCallback";
import {ConservationActionPermitModal} from "../../Modals/ConservationActionPermitModal";

// Generally rendered by ChildFieldEditor
export const StreamBankConservationActionPermitList = (props) => {
    const {
        children,
        Editable,
        AdditionalTypeProps,
        sortKey, // used by useSortedData
        Data,
        
        AddItem: addItem = () => {},
        UpdateItem=((item) => {}),
    } = props;
    const {customData} = AdditionalTypeProps.FieldDef;
    
    const { organizationId, conservationActionId } = useSelector(state => state.contextState.context);
    
    const [conservationActionPermitForEdit, setConservationActionPermitForEdit] = useState(null)
    
    const data = useSortedData(Data, sortKey);
    const filteredData = data?.filter?.(x => x?.Permit?.permitTypeId === customData?.permitTypeId) ?? [];
    
    const onValidateItem = async (inputValue) => {
        
        // if we already have an unsaved Funder with this name in the list, then don't allow it
        if(data.some(existingItem => existingItem?.Permit?.permitNumber?.toLowerCase().trim() === inputValue.toLowerCase().trim())) {
            return false;
        }
        
        const additionalParams = {
            permitTypeId: customData?.permitTypeId ?? null,
            organizationId: organizationId ?? null,
        }
        
        // if we already have a saved Permit with this name in the list, then don't allow it either
        const permits = (await PermitService.Search(inputValue, additionalParams))?.result;
        return permits?.every(permit => (permit.permitNumber ?? '').toLowerCase().trim() !== inputValue.toLowerCase().trim() );
    }
    
    // this filter determines which options are valid for selection
    // in the Permit AutoComplete within the ConservationActionPermitModal
    const filter = useAutoCompleteOptionsFilterCallback(
        PermitService.PrimaryKeyProperty,
        filteredData,
        (permit) => {
            // filter out any permits that don't match the permitTypeId
            if(String(permit.permitTypeId) !== String(customData?.permitTypeId)) {
                return false;
            }
            
            const permitNumber = permit.permitNumber.toLowerCase().trim();
            
            // filter out any filteredData that has the same permitNumber as the inputValue
            return !filteredData.some( (existingItem) => 
                existingItem?.permitNumber?.toLowerCase().trim() === permitNumber
            );
    });
    
    const onUpdateItem = useCallback((conservationActionPermit) => {
        // enforce the conservationActionId from the context
        const updatedConservationActionPermit = {
            ...conservationActionPermit,
            conservationActionId,
        };
        UpdateItem?.(updatedConservationActionPermit);
    }, [conservationActionId, UpdateItem]);
    
    const surveyTypeId = AdditionalTypeProps?.FieldDef?.customData?.surveyTypeId || null;
    
    return <Fragment>
        <ConservationActionPermitModal
            conservationActionPermit={conservationActionPermitForEdit}
            onUpdate={onUpdateItem}
            
            show={!!conservationActionPermitForEdit}
            onHide={() => setConservationActionPermitForEdit(null)}
            
            editable={Editable}
            
            
            // passed down to the PermitModal
            // so it can add appropriate File tags
            // TODO: can we do this in a cleaner way?
            // maybe by having the surveyTypeId in
            // the redux context state?
            surveyTypeId={surveyTypeId}
        />
    
         <MutableList
            {...props}
            Data={filteredData}
            showEditButton={(item) => item.permitId}
            onEditItem={setConservationActionPermitForEdit}
            AddItem={addItem}
            OnUpdateItem={onUpdateItem}
            
            AdditionalTypeProps={{
                ...AdditionalTypeProps,
                
                CustomData:{
                    ...AdditionalTypeProps.CustomData,
                    OnUpdateModel: onUpdateItem,
                    permitTypeId: customData?.permitTypeId,
                    organizationId,
                    filter,
                    onValidateItem,
                },
            }}
        />
    </Fragment>
}
